import React, { useState, useEffect } from "react";
import { PrintRecord, PrintStatus } from "./IReport";
import {
  X,
  ExternalLink,
  Printer,
  Calendar,
  User,
  BookOpen,
  MapPin,
  Clock,
} from "lucide-react";
import { StatusBadge } from "./StatusBadges";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Store/Hooks";
import {
  clearReportReprint,
  ReportReprint,
} from "../../../../Redux/Slices/Booklet/Admin/Report/ReportReprint";
import ConfirmDialog from "../ComformDialog/ConformDialog";
import { useLocalTZ } from "../../../../Hooks/useLocalTZ";

interface BookletDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  record: PrintRecord | null;
}

export const BookletDrawer: React.FC<BookletDrawerProps> = ({
  isOpen,
  onClose,
  record,
}) => {
  console.log(record, "record record");
  const { formatDateSlashed, formatTime } = useLocalTZ();
  const dispatch = useAppDispatch();
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [reprintRequested, setReprintRequested] = useState(false);
  const [Status, setStatus] = useState(false);
  const { data: ReportReprintData, isLoading: ReportReprintLoading } =
    useAppSelector((state) => state.TutorAdminReportReprint);

  useEffect(() => {
    if (reprintRequested && !ReportReprintLoading && ReportReprintData) {
      const message = ReportReprintData?.message || "Print request submitted";
      setDialogMessage(message);
      setStatus(ReportReprintData?.status || false)
      setOpenDialog(true);
      setReprintRequested(false);
    }
  }, [reprintRequested, ReportReprintLoading, ReportReprintData]);

  const handleReprint = async () => {
    if (!record) return;
    setReprintRequested(true);

    try {
      await dispatch(ReportReprint({ cart_id: record.id }));
    } catch (error: any) {
      setReprintRequested(false);
      const message = error?.message || "Something went wrong";
      setDialogMessage(message);
      setOpenDialog(true);
    }
  };

  useEffect(() => {
    dispatch(clearReportReprint());
  }, []);

  if (!record) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/30 backdrop-blur-sm z-40 transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed inset-y-0 right-0  sm:w-[480px] bg-white shadow-2xl transform transition-transform duration-300 ease-out z-50 flex flex-col ${isOpen ? "translate-x-0" : "translate-x-full"
          }`}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <h2 className="text-[16px] font-normal text-slate-800">
            Print Details
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-slate-200 text-slate-500 transition-colors"
          >
            <X className="w-[20px] h-[20px]" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar space-y-8">
          {/* Booklet Section */}
          <div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-brand-600 leading-tight">
                  {record.bookletName}
                </h3>
                <p className="text-[12px] text-slate-400 mt-1 font-mono">
                  {record.bookletDrivePath}
                </p>
              </div>
              <div className="shrink-0">
                {record.status && <StatusBadge status={record.status} />}
              </div>
            </div>

            <p className="text-[12px] text-slate-600 text-sm mt-3 leading-relaxed">
              {record.bookletDescription ||
                "Standard curriculum booklet for this week covering key concepts and practice questions."}
            </p>

            <div className="mt-5">
              <a
                href={record.bookletDriveUrl}
                target="_blank"
                rel="noreferrer"
                className=" text-[14px] inline-flex items-center gap-2 px-4 py-2 bg-brand-50 text-brand-700  font-medium rounded-md border border-brand-200 hover:bg-brand-100 transition-colors"
              >
                <ExternalLink className="w-[20px] h-[20px]" />
                Open in Google Drive
              </a>
            </div>
          </div>

          {/* Print Job Details Card */}
          <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
            <h4 className="text-[12px] font-semibold text-slate-900 uppercase tracking-wider mb-4">
              Job Info
            </h4>

            <div className="grid grid-cols-2 gap-y-4 gap-x-9">
              <div className="col-span-1">
                <label className="text-[12px] text-slate-500 block mb-1">
                  Copies
                </label>
                <div className="flex items-center gap-2 text-slate-800 font-medium">
                  <Printer className="w-4 h-4 text-slate-400" />
                  {record.printCount}
                </div>
              </div>
              <div className="col-span-1">
                <label className="text-xs text-slate-500 block mb-1">
                  Requested By
                </label>
                <div className="flex items-center gap-2 text-slate-800 font-medium">
                  <User className="w-4 h-4 text-slate-400" />
                  {record.requestedBy}
                </div>
              </div>
              <div className="col-span-2">
                <label className="text-xs text-slate-500 block mb-1">
                  Requested Date
                </label>
                <div className="flex items-center gap-2 text-slate-800 font-medium">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <div>
                    {formatDateSlashed(record.requestedDate)},
                    <span className="block">{formatTime(record.requestedDate).toUpperCase()}</span>
                  </div>
                </div>
              </div>
              {record.notes && (
                <div className="col-span-2 mt-2 pt-3 border-t border-slate-200">
                  <label className="text-xs text-slate-500 block mb-1">
                    Notes
                  </label>
                  <p className="text-sm text-slate-700 bg-yellow-50 p-2 rounded border border-yellow-100">
                    {record.notes}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Class Context Card */}
          <div className="bg-white rounded-xl p-0">
            <h4 className="text-[12px] font-semibold text-slate-900 uppercase tracking-wider mb-4">
              Class Context
            </h4>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <div className="text-[12px]  font-medium text-slate-800">
                    {record.centreName}
                  </div>
                  <div className="text-[12px]  text-slate-500">Centre</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <div className="text-[12px]  font-medium text-slate-800">
                    {record.dayOfWeek}
                  </div>
                  <div className="text-[12px] text-slate-500">Day</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <BookOpen className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <div className="text-[12px] font-medium text-slate-800">
                    {record.className}
                  </div>
                  <div className="text-[12px]  text-slate-500">Class</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <User className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <div className="text-[12px]  font-medium text-slate-800">
                    {record.tutorName}
                  </div>
                  <div className="text-[12px]  text-slate-500">
                    Assigned Tutor
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3">
          <button
            className="px-4 py-2 text-slate-700 bg-white border border-slate-300 rounded-lg shadow-sm hover:bg-slate-50 font-medium text-sm"
            onClick={onClose}
          >
            Close
          </button>
          <button
            onClick={handleReprint}
            disabled={ReportReprintLoading}
            className="px-4 py-2 bg-[#009ddf] text-white bg-brand-600 rounded-lg shadow-sm hover:bg-brand-700 font-medium text-sm flex items-center gap-2"
          >
            {ReportReprintLoading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Printer className="w-4 h-4" />
            )}
            {ReportReprintLoading ? "Processing..." : "Print Again (Reuse)"}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={openDialog}
        message={dialogMessage}
        status={Status}
        onClose={() => setOpenDialog(false)}
        onConfirm={() => {
          setOpenDialog(false);
          onClose();
        }}
      />
    </>
  );
};
